import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface CopyData {
  [key: string]: any;
}

interface CopyContextType {
  copy: CopyData;
  isLoading: boolean;
  getCopy: (path: string) => any;
}

const CopyContext = createContext<CopyContextType | undefined>(undefined);

interface CopyProviderProps {
  children: ReactNode;
}

export const CopyProvider: React.FC<CopyProviderProps> = ({ children }) => {
  const [copy, setCopy] = useState<CopyData>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCopy = async () => {
      try {
        const response = await fetch('/copy.json');
        const copyData = await response.json();
        setCopy(copyData);
      } catch (error) {
        console.error('Failed to load copy data:', error);
        // Fallback to empty object if copy fails to load
        setCopy({});
      } finally {
        setIsLoading(false);
      }
    };

    loadCopy();
  }, []);

  const getCopy = (path: string): any => {
    const keys = path.split('.');
    let result = copy;
    
    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = result[key];
      } else {
        console.warn(`Copy path not found: ${path}`);
        return '';
      }
    }
    
    return result;
  };

  return (
    <CopyContext.Provider value={{ copy, isLoading, getCopy }}>
      {children}
    </CopyContext.Provider>
  );
};

export const useCopy = (): CopyContextType => {
  const context = useContext(CopyContext);
  if (context === undefined) {
    throw new Error('useCopy must be used within a CopyProvider');
  }
  return context;
};
